import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { AuditLog } from '../model/audit-log.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as XLSX from 'xlsx';

interface ColumnConfig {
  key: string;
  label: string;
}

@Component({
  selector: 'app-meter-changes',
  templateUrl: './meter-changes.component.html',
  styleUrls: ['./meter-changes.component.scss']
})
export class MeterChangesComponent implements OnInit {
  allChanges: AuditLog[] = [];
  filteredChanges: AuditLog[] = [];
  loading = false;
  searchTerm = '';
  sortColumn = 'performedAt';
  sortDirection: 'asc' | 'desc' = 'desc';

  columns: ColumnConfig[] = [
    { key: 'performedAt', label: 'Date' },
    { key: 'performedBy', label: 'Performed By' },
    { key: 'entityId', label: 'Meter ID' },
    { key: 'fieldName', label: 'Field' },
    { key: 'oldValue', label: 'Old Value' },
    { key: 'newValue', label: 'New Value' },
    { key: 'description', label: 'Description' },
  ];

  constructor(private api: ApiService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.api.getMeterChanges().subscribe({
      next: (data) => {
        this.allChanges = data || [];
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Error loading meter changes', 'Close', { duration: 3000 });
      }
    });
  }

  get totalChanges(): number {
    return this.filteredChanges.length;
  }

  get readingReversions(): number {
    return this.filteredChanges.filter(c => c.fieldName === 'currentReading').length;
  }

  get serialNumberChanges(): number {
    return this.filteredChanges.filter(c => c.fieldName === 'serialNumber').length;
  }

  applyFilter(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredChanges = [...this.allChanges];
    } else {
      const term = this.searchTerm.toLowerCase().trim();
      this.filteredChanges = this.allChanges.filter(c =>
        this.columns.some(col => {
          const val = (c as any)[col.key];
          return val != null && String(val).toLowerCase().includes(term);
        })
      );
    }
    this.applySort();
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  applySort(): void {
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredChanges = [...this.filteredChanges].sort((a, b) => {
      const aVal = (a as any)[this.sortColumn] ?? '';
      const bVal = (b as any)[this.sortColumn] ?? '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      return ((aVal as any) - (bVal as any)) * direction;
    });
  }

  getValue(row: AuditLog, key: string): any {
    if (key === 'performedBy') {
      return this.getPerformedBy((row as any)[key]);
    }
    return (row as any)[key];
  }

  getPerformedBy(value: string): string {
    const loggedInUser = localStorage.getItem('loggedInUser');
    const loggedInUserName = localStorage.getItem('loggedInUserName');
    if (value === 'system' && loggedInUserName) {
      return loggedInUserName;
    }
    if (value === loggedInUser && loggedInUserName) {
      return loggedInUserName;
    }
    return value || '-';
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  exportToExcel(): void {
    const rows = this.filteredChanges.map(c => ({
      Date: c.performedAt,
      'Performed By': this.getPerformedBy(c.performedBy),
      'Meter ID': c.entityId,
      Field: c.fieldName,
      'Old Value': c.oldValue,
      'New Value': c.newValue,
      Description: c.description
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Meter Changes');
    XLSX.writeFile(wb, `meter-changes-${new Date().toISOString().slice(0, 10)}.xlsx`);
    this.snackBar.open('Excel exported', 'Close', { duration: 2000 });
  }

  exportToPDF(): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }
    const rows = this.filteredChanges.map(c => `
      <tr>
        <td>${c.performedAt || ''}</td>
        <td>${this.getPerformedBy(c.performedBy)}</td>
        <td>${c.entityId || ''}</td>
        <td>${c.fieldName || ''}</td>
        <td>${c.oldValue || ''}</td>
        <td>${c.newValue || ''}</td>
        <td>${c.description || ''}</td>
      </tr>
    `).join('');
    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Meter Changes Report</title>
  <style>
    @page { size: landscape; margin: 12mm; }
    body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 24px; color: #1a1a2e; background: #fff; }
    .header { border-bottom: 3px solid #0f766e; padding-bottom: 14px; margin-bottom: 24px; }
    .header h1 { color: #0f766e; margin: 0; font-size: 24px; }
    .header .subtitle { font-size: 13px; color: #64748b; }
    .header .meta { font-size: 12px; color: #475569; margin-top: 8px; }
    table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 10px; margin-top: 16px; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
    th, td { border-bottom: 1px solid #e2e8f0; padding: 8px; }
    th { background: #0f766e; color: #fff; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; text-align: left; }
    td { background: #fff; color: #1e293b; }
    tr:nth-child(even) td { background: #f8fafc; }
    .footer { margin-top: 24px; font-size: 10px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 12px; }
  </style>
</head>
<body>
  <div class='header'>
    <h1>Meter Changes Report</h1>
    <div class='subtitle'>Current reading lowered or serial number changed</div>
    <div class='meta'>
      <strong>Generated:</strong> ${new Date().toLocaleString()}<br>
      <strong>Total:</strong> ${this.filteredChanges.length}
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th>Performed By</th>
        <th>Meter ID</th>
        <th>Field</th>
        <th>Old Value</th>
        <th>New Value</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>${rows || '<tr><td colspan="7" style="text-align:center">No data</td></tr>'}</tbody>
  </table>
  <div class='footer'>SomWater Meter Changes — Confidential</div>
</body>
</html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 150);
  }
}
